import React, { useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMicrophone, faMicrophoneSlash, faVideo, faVideoSlash, faDesktop, faPhoneSlash, faUsers, faVolumeHigh,
    faComment, faLock, faLockOpen, faPaperPlane, faUserSlash, faStopCircle, faSignal, faXmark, faLink, faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import MeetingPhoto from './MeetingPhoto';
import SpeakerMenu from './SpeakerMenu';
import MeetingInviteModal from './MeetingInviteModal';
import { copyLink } from '../../utils/copyLink';
import { fitTiles } from '../../utils/fitTiles';
import ChatLines from './ChatLines';
import '../../styles/meetings.css';

/**
 * The meeting itself: everyone's tiles, a shared screen when there is one, the
 * controls, the list of people (with host controls for hosts), and the chat.
 *
 * `client` is how it talks to the server (utils/meetingApi.js) - the same
 * screen serves employees and outside guests.
 */

/** Attach a LiveKit video track to a <video> element, and let go of it on the way out. */
const TrackVideo = ({ track, muted = true, className }) => {
    const ref = useRef(null);
    const [tall, setTall] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el || !track) return undefined;
        // A tall picture (a phone held upright) is shown whole, never stretched to fill a wide tile.
        const measure = () => setTall(el.videoHeight > el.videoWidth * 1.05);
        el.addEventListener('loadedmetadata', measure);
        el.addEventListener('resize', measure);
        track.attach(el);
        return () => {
            el.removeEventListener('loadedmetadata', measure);
            el.removeEventListener('resize', measure);
            track.detach(el);
        };
    }, [track]);
    return <video ref={ref} className={`${className || ''} ${tall ? 'tall' : ''}`} muted={muted} playsInline autoPlay />;
};

const QUALITY_LABELS = { excellent: 'Excellent connection', good: 'Good connection', poor: 'Poor connection', lost: 'Connection lost' };

const Quality = ({ value }) => {
    if (!QUALITY_LABELS[value] || value === 'excellent') return null;
    return (
        <span className={`mt-quality ${value}`} title={QUALITY_LABELS[value]}>
            <FontAwesomeIcon icon={faSignal} />
        </span>
    );
};

const Tile = ({ person, big = false }) => (
    <div className={`mt-tile ${person.speaking ? 'speaking' : ''} ${big ? 'big' : ''}`}>
        {person.camOn
            ? <TrackVideo track={person.cameraTrack} className={`mt-tile-video ${person.isLocal ? 'mirror' : ''}`} />
            : <div className="mt-tile-avatar"><MeetingPhoto src={person.photo} name={person.name} className="mt-tile-avatar-img" /></div>}
        <div className="mt-tile-name">
            {!person.micOn && <FontAwesomeIcon icon={faMicrophoneSlash} />}
            <span>{person.isLocal ? 'You' : person.name}</span>
            <Quality value={person.quality} />
        </div>
    </div>
);

const ControlButton = ({ icon, label, on = true, danger = false, onClick, disabled, badge = 0 }) => (
    <button
        type="button"
        className={`mt-control ${on ? '' : 'off'} ${danger ? 'danger' : ''}`}
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        title={label}
    >
        <FontAwesomeIcon icon={icon} />
        {badge > 0 && <span className="mt-control-badge">{badge > 9 ? '9+' : badge}</span>}
    </button>
);

/** The chat: lines so far, and a box to add one. */
/** A side panel's title, with a way to put it away - on a phone the panel covers the whole picture. */
const PanelHead = ({ title, onClose }) => (
    <div className="mt-panel-head">
        <h4>{title}</h4>
        <button type="button" className="mt-icon-btn" onClick={onClose} aria-label={`Close ${String(title).split(' ')[0].toLowerCase()}`}>
            <FontAwesomeIcon icon={faXmark} />
        </button>
    </div>
);

const ChatPanel = ({ chat, onClose }) => {
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const endRef = useRef(null);

    useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [chat.messages.length]);

    const submit = async (e) => {
        e.preventDefault();
        if (!text.trim() || sending) return;
        setSending(true);
        try {
            await chat.send(text);
            setText('');
        } catch (err) {
            Swal.fire('Not sent', err.response?.data?.message || 'Could not send that.', 'error');
        } finally {
            setSending(false);
        }
    };

    return (
        <aside className="mt-side-panel">
            <PanelHead title="Chat" onClose={onClose} />
            <div className="mt-chat-lines">
                {chat.messages.length === 0 && <p className="mt-subtle">No messages yet.</p>}
                <ChatLines messages={chat.messages} withTime />
                <div ref={endRef} />
            </div>
            {chat.error && <p className="mt-subtle">{chat.error}</p>}
            <form className="mt-chat-form" onSubmit={submit}>
                <input
                    type="text"
                    value={text}
                    maxLength={2000}
                    placeholder="Message everyone"
                    onChange={(e) => setText(e.target.value)}
                />
                <button type="submit" className="mt-icon-btn" disabled={sending || !text.trim()} aria-label="Send">
                    <FontAwesomeIcon icon={faPaperPlane} />
                </button>
            </form>
        </aside>
    );
};

/**
 * Who is asking to be let in. Hosts poll for it for as long as they are in the room - not
 * only while the People panel is open, or somebody could wait at the door unseen.
 */
const useLobby = (client, canHost) => {
    const [waiting, setWaiting] = useState([]);
    useEffect(() => {
        if (!canHost || !client.lobby) return undefined;
        let alive = true;
        let timer = null;
        const tick = async () => {
            try {
                const rows = await client.lobby();
                if (alive) setWaiting(rows);
            } catch { /* try again next time */ }
            if (alive) timer = setTimeout(tick, 3000);
        };
        tick();
        return () => { alive = false; clearTimeout(timer); };
    }, [canHost, client]);

    const decide = async (entry, decision) => {
        try {
            await client.decide(entry._id, decision);
        } catch (err) {
            Swal.fire('Could not do that', err.response?.data?.message || 'Please try again.', 'error');
        }
        setWaiting((rows) => rows.filter((r) => r._id !== entry._id));
    };

    return { waiting, decide };
};

/** Who is here, with mute / remove for hosts, and the waiting room. */
const PeoplePanel = ({ participants, client, canHost, locked, onLock, onClose, waiting = [], decide }) => {
    const act = async (fn, failure) => {
        try { await fn(); } catch (err) { Swal.fire('Could not do that', err.response?.data?.message || failure, 'error'); }
    };

    const remove = async (person) => {
        const result = await Swal.fire({
            title: `Remove ${person.name}?`,
            text: 'They will not be able to rejoin this meeting.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Remove',
            confirmButtonColor: '#dc2626'
        });
        if (result.isConfirmed) act(() => client.remove(person.identity), 'Could not remove them.');
    };

    return (
        <aside className="mt-side-panel">
            <PanelHead title={`People (${participants.length})`} onClose={onClose} />

            {canHost && waiting.length > 0 && (
                <div className="mt-lobby">
                    <strong>Waiting to join</strong>
                    {waiting.map((w) => (
                        <div key={w._id} className="mt-lobby-row">
                            <div className="mt-person"><span>{w.name}</span>{w.email && <small>{w.email}</small>}</div>
                            <button type="button" className="mt-btn small primary" onClick={() => decide(w, 'admit')}>Admit</button>
                            <button type="button" className="mt-btn small ghost" onClick={() => decide(w, 'deny')}>Deny</button>
                        </div>
                    ))}
                </div>
            )}

            <ul className="mt-people">
                {participants.map((p) => (
                    <li key={p.identity}>
                        <MeetingPhoto src={p.photo} name={p.name} className="mt-mini-avatar" />
                        <div className="mt-person"><span>{p.isLocal ? `${p.name} (you)` : p.name}</span></div>
                        {p.screenOn && <FontAwesomeIcon icon={faDesktop} className="mt-subtle" />}
                        {canHost && !p.isLocal && p.micOn && (
                            <button
                                type="button"
                                className="mt-icon-btn"
                                title={`Mute ${p.name}`}
                                aria-label={`Mute ${p.name}`}
                                onClick={() => act(() => client.mute(p.identity), 'Could not mute them.')}
                            >
                                <FontAwesomeIcon icon={faMicrophoneSlash} />
                            </button>
                        )}
                        {!p.micOn && <FontAwesomeIcon icon={faMicrophoneSlash} className="mt-subtle" />}
                        {canHost && !p.isLocal && (
                            <button
                                type="button"
                                className="mt-icon-btn danger"
                                title={`Remove ${p.name}`}
                                aria-label={`Remove ${p.name}`}
                                onClick={() => remove(p)}
                            >
                                <FontAwesomeIcon icon={faUserSlash} />
                            </button>
                        )}
                    </li>
                ))}
            </ul>

            {canHost && (
                <button type="button" className="mt-btn ghost mt-lock-btn" onClick={onLock}>
                    <FontAwesomeIcon icon={locked ? faLockOpen : faLock} /> {locked ? 'Unlock the meeting' : 'Lock the meeting'}
                </button>
            )}
        </aside>
    );
};

/** The size of an element, kept up to date. */
const useBoxSize = () => {
    const ref = useRef(null);
    const [size, setSize] = useState({ width: 0, height: 0 });
    useEffect(() => {
        const el = ref.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const measure = () => setSize((old) => (old.width === el.clientWidth && old.height === el.clientHeight
            ? old : { width: el.clientWidth, height: el.clientHeight }));
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    });
    return [ref, size];
};

/** True on a phone-width screen, where the shared screen keeps its people strip underneath. */
const usePhoneWidth = () => {
    const query = '(max-width: 760px)';
    const [phone, setPhone] = useState(() => Boolean(window.matchMedia?.(query).matches));
    useEffect(() => {
        const list = window.matchMedia?.(query);
        if (!list) return undefined;
        const on = (e) => setPhone(e.matches);
        list.addEventListener?.('change', on);
        return () => list.removeEventListener?.('change', on);
    }, []);
    return phone;
};

const MeetingRoomView = ({
    title, room, session, canHost = false, onLeave, embedded = false, shareLink = '', inviteMeetingId = null, endWarning = null
}) => {
    const { client, panel, setPanel, locked, setLocked, chat, notice, dismissNotice } = session;
    const { participants } = room;
    const phone = usePhoneWidth();
    const { waiting, decide } = useLobby(client, canHost);
    const [inviting, setInviting] = useState(false);
    const [copied, setCopied] = useState(false);

    const copyMeetingLink = async () => {
        if (await copyLink(shareLink)) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const presenter = participants.find((p) => p.screenOn);
    // While somebody presents, the people stand in a column on the right; opening chat (or the
    // list of people) puts it in that same column instead of the faces, and closing it brings
    // them back. On a phone there is no room for a column: the strip stays underneath and the
    // panel opens over the picture.
    const railLayout = Boolean(presenter) && !phone;
    // Wide tiles on a computer, square ones on a phone (one person: a little wider than tall).
    const ratio = phone ? (participants.length === 1 ? 4 / 3 : 1) : 16 / 9;
    const [gridRef, box] = useBoxSize();
    const fit = fitTiles(participants.length, box.width, box.height, ratio);

    // Once the person who was asked to stop has stopped, the banner has done its job.
    useEffect(() => { if (notice && !room.sharing) dismissNotice(); }, [notice, room.sharing, dismissNotice]);

    /**
     * One screen at a time. Starting a share asks the server first: if somebody
     * else is presenting, this person is told who to ask, and that person is
     * told - by a banner on their screen and a line in the chat - that somebody
     * is waiting.
     */
    const toggleShare = async () => {
        if (room.sharing) { room.toggleScreenShare(); return; }

        const other = participants.find((p) => p.screenOn && !p.isLocal);
        let verdict = { allowed: true };
        try { verdict = await client.requestShare(); } catch { /* if the check itself fails, do not block the share */ }

        if (verdict.forbidden) {
            Swal.fire('Screen sharing is off', 'The host has switched off screen sharing for guests.', 'info');
            return;
        }
        if (!verdict.allowed || other) {
            const who = verdict.presenterName || other?.name || 'Somebody';
            Swal.fire(
                'Someone is already presenting',
                `${who} is already sharing their screen. Ask them to stop, then try again. They have been told you are waiting.`,
                'info'
            );
            return;
        }
        room.toggleScreenShare();
    };

    const extend = async (minutes) => {
        try {
            await endWarning.onExtend(minutes);
        } catch (err) {
            Swal.fire('Could not extend', err.response?.data?.message || 'Please try again.', 'error');
        }
    };

    const toggleLock = async () => {
        try {
            const res = await client.setLock(!locked);
            setLocked(res.locked);
        } catch (err) {
            Swal.fire('Could not change the lock', err.response?.data?.message || 'Please try again.', 'error');
        }
    };

    const endForAll = async () => {
        const result = await Swal.fire({
            title: 'End the meeting for everyone?',
            text: 'Everyone will be disconnected.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'End for all',
            confirmButtonColor: '#dc2626'
        });
        if (!result.isConfirmed) return;
        try { await client.end(); } catch (err) {
            Swal.fire('Could not end it', err.response?.data?.message || 'Please try again.', 'error');
        }
    };

    // The side panels. Chat stays mounted whenever the room is, so lines keep arriving (and
    // are counted) while it is closed.
    const panels = (
        <>
            {panel === 'people' && (
                <PeoplePanel
                    participants={participants}
                    client={client}
                    canHost={canHost}
                    locked={locked}
                    onLock={toggleLock}
                    onClose={() => setPanel(null)}
                    waiting={waiting}
                    decide={decide}
                />
            )}
            <div style={{ display: panel === 'chat' ? 'contents' : 'none' }}><ChatPanel chat={chat} onClose={() => setPanel(null)} /></div>
        </>
    );

    return (
        <div className={`mt-room ${embedded ? 'embedded' : ''}`}>
            {inviting && (
                <MeetingInviteModal meetingId={inviteMeetingId} link={shareLink} onClose={() => setInviting(false)} />
            )}
            {canHost && waiting.length > 0 && panel !== 'people' && (
                <div className="mt-lobby-banner" role="alert">
                    <span>
                        <strong>{waiting[0].name}</strong>
                        {waiting.length > 1 ? ` and ${waiting.length - 1} more are` : ' is'} waiting to join
                    </span>
                    <span className="mt-share-notice-actions">
                        {waiting.length === 1 ? (
                            <>
                                <button type="button" className="mt-btn small primary" onClick={() => decide(waiting[0], 'admit')}>Admit</button>
                                <button type="button" className="mt-btn small ghost" onClick={() => decide(waiting[0], 'deny')}>Deny</button>
                            </>
                        ) : (
                            <button type="button" className="mt-btn small primary" onClick={() => setPanel('people')}>See who</button>
                        )}
                    </span>
                </div>
            )}
            {endWarning && (
                <div className="mt-end-banner" role="alert">
                    <span>
                        {endWarning.minutesLeft > 0
                            ? `This meeting ends in ${endWarning.minutesLeft} ${endWarning.minutesLeft === 1 ? 'minute' : 'minutes'}.`
                            : 'The scheduled time is over. The meeting carries on until you end it.'}
                    </span>
                    <span className="mt-share-notice-actions">
                        {[15, 30].map((minutes) => (
                            <button key={minutes} type="button" className="mt-btn small primary" onClick={() => extend(minutes)}>
                                +{minutes} min
                            </button>
                        ))}
                        <button type="button" className="mt-btn small ghost" onClick={endWarning.onDismiss}>Dismiss</button>
                    </span>
                </div>
            )}
            {notice && (
                <div className="mt-share-notice" role="alert">
                    <span>{notice.text}</span>
                    <span className="mt-share-notice-actions">
                        <button type="button" className="mt-btn small primary" onClick={() => { room.toggleScreenShare(); dismissNotice(); }}>
                            Stop sharing
                        </button>
                        <button type="button" className="mt-btn small ghost" onClick={dismissNotice}>Dismiss</button>
                    </span>
                </div>
            )}

            <div className="mt-room-head">
                <strong>{title}{locked ? ' · locked' : ''}</strong>
                <span className="mt-room-head-right">
                    <span className="mt-subtle">
                        {participants.length} {participants.length === 1 ? 'person' : 'people'}
                        {room.status === 'reconnecting' ? ' · Reconnecting…' : ''}
                    </span>
                    {shareLink && (
                        <button type="button" className="mt-head-btn" onClick={copyMeetingLink} title="Copy the meeting link">
                            <FontAwesomeIcon icon={faLink} /> {copied ? 'Copied' : 'Copy link'}
                        </button>
                    )}
                    {inviteMeetingId && (
                        <button type="button" className="mt-head-btn" onClick={() => setInviting(true)} title="Invite people">
                            <FontAwesomeIcon icon={faUserPlus} /> Invite
                        </button>
                    )}
                </span>
            </div>

            {/* The browser held the sound back (phones do, until a tap). A big
                button in the middle cannot be missed, unlike a bar at the top. */}
            {room.audioBlocked && (
                <div className="mt-sound-gate">
                    <button type="button" className="mt-btn primary" onClick={room.enableAudio}>
                        <FontAwesomeIcon icon={faVolumeHigh} /> Tap to hear the meeting
                    </button>
                </div>
            )}

            <div className="mt-room-body">
                <div className={`mt-stage ${presenter ? 'with-screen' : ''}`}>
                    {presenter ? (
                        <>
                            <div className="mt-screen">
                                <TrackVideo track={presenter.screenTrack} className="mt-screen-video" />
                                <div className="mt-tile-name"><span>{presenter.isLocal ? 'You are' : `${presenter.name} is`} presenting</span></div>
                            </div>
                            {railLayout ? (
                                <div className="mt-rail">
                                    {/* Chat (or the people list) takes the column's place while it is open,
                                        and the faces come back when it is closed. */}
                                    {!panel && (
                                        <div className="mt-rail-people">
                                            {participants.map((p) => <Tile key={p.identity} person={p} />)}
                                        </div>
                                    )}
                                    {panels}
                                </div>
                            ) : (
                                <div className="mt-strip">
                                    {participants.map((p) => <Tile key={p.identity} person={p} />)}
                                </div>
                            )}
                        </>
                    ) : (
                        <div
                            ref={gridRef}
                            className="mt-grid"
                            style={{
                                gridTemplateColumns: fit.tile ? `repeat(${fit.columns}, ${fit.tile}px)` : `repeat(${fit.columns}, minmax(0, 1fr))`,
                                '--tile-ratio': ratio
                            }}
                        >
                            {participants.map((p) => <Tile key={p.identity} person={p} big={participants.length === 1} />)}
                        </div>
                    )}
                </div>

                {!railLayout && panels}
            </div>

            <div className="mt-controls" aria-live="polite">
                <ControlButton
                    icon={room.micOn ? faMicrophone : faMicrophoneSlash}
                    label={room.micOn ? 'Mute microphone' : 'Unmute microphone'}
                    on={room.micOn}
                    onClick={room.toggleMic}
                />
                <ControlButton
                    icon={room.camOn ? faVideo : faVideoSlash}
                    label={room.camOn ? 'Turn camera off' : 'Turn camera on'}
                    on={room.camOn}
                    onClick={room.toggleCamera}
                />
                {room.canShareScreen && (
                    <ControlButton
                        icon={faDesktop}
                        label={room.sharing ? 'Stop sharing' : 'Share your screen'}
                        on={!room.sharing}
                        onClick={toggleShare}
                    />
                )}
                <SpeakerMenu
                    supported={room.outputSupported}
                    outputs={room.outputs}
                    current={room.outputId}
                    onSelect={room.switchOutput}
                    onPickRoute={room.canPickRoute ? room.pickRoute : null}
                    routes={room.routes}
                    routeId={room.routeId}
                    onRoute={room.switchRoute}
                    noise={room.denoiseSupported ? { on: room.denoiseOn, onToggle: room.toggleDenoise } : null}
                />
                <ControlButton
                    icon={faUsers}
                    label="People"
                    on={panel !== 'people'}
                    onClick={() => setPanel((p) => (p === 'people' ? null : 'people'))}
                />
                <ControlButton
                    icon={faComment}
                    label="Chat"
                    on={panel !== 'chat'}
                    badge={panel === 'chat' ? 0 : chat.unread}
                    onClick={() => setPanel((p) => (p === 'chat' ? null : 'chat'))}
                />
                {canHost && <ControlButton icon={faStopCircle} label="End meeting for everyone" onClick={endForAll} />}
                <ControlButton icon={faPhoneSlash} label="Leave meeting" danger onClick={onLeave} />
            </div>
        </div>
    );
};

export default MeetingRoomView;
